// AbsenceOverview.js - Översikt över sjukdom och semester.
// Egen sida under Admin → Frånvaro (Spår K2.4), tidigare modal i veckovyn.
import React, { useState, useEffect, useContext } from 'react';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import { isClinicLevelAdmin } from '../../utils/permissions';
import './AbsenceOverview.css';

const AbsenceOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'sick', 'vacation'

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/api/calendar/absence-overview', {
        params: { type: filter }
      });
      setData(response.data);
    } catch (err) {
      console.error('Error loading absence overview:', err);
      setError('Kunde inte ladda frånvaroöversikt');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    const date = new Date(timeStr);
    return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatDateRange = (startStr, endStr) => {
    const start = new Date(startStr);
    const end = new Date(endStr);

    // Samma dag?
    if (start.toDateString() === end.toDateString()) {
      return `${formatDate(startStr)} ${formatTime(startStr)} - ${formatTime(endStr)}`;
    }

    // Olika dagar
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return `${formatDate(startStr)} - ${formatDate(endStr)} (${days} dagar)`;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'sick':
        return 'fa-thermometer-half';
      case 'vacation':
        return 'fa-umbrella-beach';
      default:
        return 'fa-calendar-times';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'sick':
        return 'Sjuk';
      case 'vacation':
        return 'Semester';
      default:
        return 'Frånvarande';
    }
  };

  const getTypeClass = (type) => {
    return type === 'sick' ? 'sick' : 'vacation';
  };

  if (loading) {
    return (
      <div className="absence-overview-page">
        <div className="absence-overview-card">
          <div className="absence-loading">
            <i className="fas fa-spinner fa-spin"></i> Laddar...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absence-overview-page">
      <div className="absence-overview-card">
        {/* Header */}
        <div className="absence-header">
          <div className="absence-header-left">
            <h2>
              <i className="fas fa-users-slash"></i>
              Frånvaroöversikt
            </h2>
          </div>
          <div className="absence-header-right">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="absence-filter"
            >
              <option value="all">Alla typer</option>
              <option value="sick">Endast sjukdom</option>
              <option value="vacation">Endast semester</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="absence-error">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}

        {data && (
          <div className="absence-content">
            {/* Sammanfattning */}
            <div className="absence-summary">
              <div className="summary-item absent">
                <span className="summary-number">{data.summary.absent_today}</span>
                <span className="summary-label">Borta idag</span>
              </div>
              <div className="summary-item available">
                <span className="summary-number">{data.summary.available_today}</span>
                <span className="summary-label">Tillgängliga</span>
              </div>
              <div className="summary-item total">
                <span className="summary-number">{data.summary.total_users}</span>
                <span className="summary-label">Totalt</span>
              </div>
            </div>

            {/* Borta idag */}
            <div className="absence-section">
              <h3>
                <i className="fas fa-user-clock"></i>
                Borta idag
                {data.today_absent.length > 0 && (
                  <span className="section-count">{data.today_absent.length}</span>
                )}
              </h3>

              {data.today_absent.length === 0 ? (
                <div className="absence-empty">
                  <i className="fas fa-check-circle"></i>
                  <span>Alla är tillgängliga idag!</span>
                </div>
              ) : (
                <div className="absence-list">
                  {data.today_absent.map((absence, idx) => (
                    <div key={`today-${idx}`} className={`absence-item ${getTypeClass(absence.type)}`}>
                      <div className="absence-icon">
                        <i className={`fas ${getTypeIcon(absence.type)}`}></i>
                      </div>
                      <div className="absence-info">
                        <span className="absence-name">{absence.user_name}</span>
                        <span className="absence-title">{absence.title}</span>
                      </div>
                      <div className="absence-time">
                        <span className="absence-type-badge">{getTypeLabel(absence.type)}</span>
                        <span className="absence-hours">
                          {formatTime(absence.start_time)} - {formatTime(absence.end_time)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Kommande frånvaro */}
            <div className="absence-section">
              <h3>
                <i className="fas fa-calendar-alt"></i>
                Kommande frånvaro
                {data.upcoming_absent.length > 0 && (
                  <span className="section-count">{data.upcoming_absent.length}</span>
                )}
              </h3>

              {data.upcoming_absent.length === 0 ? (
                <div className="absence-empty">
                  <i className="fas fa-calendar-check"></i>
                  <span>Ingen planerad frånvaro</span>
                </div>
              ) : (
                <div className="absence-list">
                  {data.upcoming_absent.map((absence, idx) => (
                    <div key={`upcoming-${idx}`} className={`absence-item ${getTypeClass(absence.type)}`}>
                      <div className="absence-icon">
                        <i className={`fas ${getTypeIcon(absence.type)}`}></i>
                      </div>
                      <div className="absence-info">
                        <span className="absence-name">{absence.user_name}</span>
                        <span className="absence-title">{absence.title}</span>
                      </div>
                      <div className="absence-time">
                        <span className="absence-type-badge">{getTypeLabel(absence.type)}</span>
                        <span className="absence-date">
                          {formatDateRange(absence.start_time, absence.end_time)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tillgängliga idag */}
            <div className="absence-section available-section">
              <h3>
                <i className="fas fa-user-check"></i>
                Tillgängliga idag
                <span className="section-count">{data.available_today.length}</span>
              </h3>

              <div className="available-list">
                {data.available_today.map((user) => (
                  <span key={user.user_id} className="available-user">
                    {user.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Bara klinikadmin + global admin (samma som övriga adminsidor) - även vid
// direkt URL. Backend nekar också (403).
const AbsenceOverviewPage = () => {
  const { user } = useContext(UserContext);
  if (!isClinicLevelAdmin(user)) {
    return (
      <div className="absence-overview-page">
        <div className="access-denied">
          <i className="fas fa-lock"></i>
          <h2>Åtkomst nekad</h2>
          <p>Endast administratörer kan se frånvaroöversikten.</p>
        </div>
      </div>
    );
  }
  return <AbsenceOverview />;
};

export default AbsenceOverviewPage;
